"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, ArrowRight, LogIn } from "lucide-react";
import Link from "next/link";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { APP_LOGO_PATH } from "@/lib/constants";
import { WHATSAPP_CONSULTOR_GENERAL } from "@/lib/constants";

const NAV_LINKS = [
  { label: "Funcionalidades", href: "#funcionalidades" },
  { label: "Como funciona", href: "#como-funciona" },
  { label: "Depoimentos", href: "#depoimentos" },
] as const;

type NavbarProps = {
  onOpenDiagnostic?: () => void;
};

const handleDemoClick = (onOpenDiagnostic?: () => void) => {
  if (onOpenDiagnostic) {
    onOpenDiagnostic();
    return;
  }
  if (typeof window !== "undefined") {
    window.open(
      `https://wa.me/${WHATSAPP_CONSULTOR_GENERAL}?text=${encodeURIComponent(
        "Olá! Gostaria de agendar uma demonstração do NoWey CRM."
      )}`,
      "_blank",
      "noopener,noreferrer"
    );
  }
};

const handleAnchorClick = (
  e: React.MouseEvent<HTMLAnchorElement>,
  href: string,
  onClose?: () => void
) => {
  e.preventDefault();
  onClose?.();
  const id = href.replace("#", "");
  const target = document.getElementById(id);
  if (target) {
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

export default function Navbar({ onOpenDiagnostic }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ opacity: 0, y: -24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          "fixed inset-x-0 top-0 z-50 transition-all duration-300",
          scrolled
            ? "bg-background/80 backdrop-blur-xl border-b border-border/60 shadow-[0_4px_32px_rgba(0,0,0,0.12)]"
            : "bg-transparent"
        )}
      >
        <nav
          className="container max-w-6xl mx-auto px-6 h-16 flex items-center justify-between"
          aria-label="Navegação principal"
        >
          {/* Logo */}
          <a
            href="/"
            className="flex items-center gap-2.5 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
            aria-label="NoWey — página inicial"
          >
            <img
              src={APP_LOGO_PATH}
              alt="NoWey"
              className="h-7 w-7 object-contain"
            />
            <span className="font-nowey font-bold text-lg tracking-tight text-foreground">
              No<span className="text-primary">Wey</span>
            </span>
          </a>

          {/* Links centrais (desktop) */}
          <ul className="hidden md:flex items-center gap-1" role="list">
            {NAV_LINKS.map(({ label, href }) => (
              <li key={href}>
                <a
                  href={href}
                  onClick={(e) => handleAnchorClick(e, href)}
                  className="px-4 py-2 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>

          {/* CTA + login + hamburguer */}
          <div className="flex items-center gap-2">
            {/* Login — desktop */}
            <Link
              href="/login"
              className="hidden md:inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Entrar na plataforma"
            >
              <LogIn className="w-3.5 h-3.5" aria-hidden />
              Entrar
            </Link>

            <Button
              type="button"
              onClick={() => handleDemoClick(onOpenDiagnostic)}
              className="hidden md:inline-flex h-9 px-5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-sm shadow-sm hover:shadow-md transition-all duration-300 gap-1.5"
              aria-label="Quero uma demo"
            >
              Quero uma demo
              <ArrowRight className="w-3.5 h-3.5" />
            </Button>

            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Abrir menu de navegação"
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
            >
              <Menu className="w-5 h-5" aria-hidden />
            </button>
          </div>
        </nav>
      </motion.header>

      {/* Sheet mobile */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          id="mobile-nav"
          side="right"
          className="w-72 bg-background border-l border-border flex flex-col pt-16 gap-0"
        >
          {/* Logo no sheet */}
          <div className="absolute left-6 top-4 flex items-center gap-2">
            <img
              src={APP_LOGO_PATH}
              alt="NoWey"
              className="h-6 w-6 object-contain"
            />
            <span className="font-nowey font-bold text-base tracking-tight text-foreground">
              No<span className="text-primary">Wey</span>
            </span>
          </div>

          {/* Links */}
          <AnimatePresence>
            {mobileOpen && (
              <motion.ul
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={{
                  visible: { transition: { staggerChildren: 0.06 } },
                  hidden: {},
                }}
                className="flex flex-col gap-1 px-2"
                role="list"
              >
                {NAV_LINKS.map(({ label, href }) => (
                  <motion.li
                    key={href}
                    variants={{
                      visible: { opacity: 1, x: 0 },
                      hidden: { opacity: 0, x: 16 },
                    }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                  >
                    <a
                      href={href}
                      onClick={(e) =>
                        handleAnchorClick(e, href, () => setMobileOpen(false))
                      }
                      className="flex items-center w-full px-4 py-3 rounded-xl text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {label}
                    </a>
                  </motion.li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>

          {/* CTAs mobile */}
          <div className="mt-auto px-2 pb-6 flex flex-col gap-2">
            <Link
              href="/login"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-2 h-11 w-full rounded-full border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Entrar na plataforma"
            >
              <LogIn className="w-4 h-4" aria-hidden />
              Entrar na plataforma
            </Link>

            <Button
              type="button"
              onClick={() => {
                setMobileOpen(false);
                handleDemoClick(onOpenDiagnostic);
              }}
              className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-sm shadow-sm hover:shadow-md transition-all duration-300 gap-2"
              aria-label="Quero uma demo"
            >
              Quero uma demo
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
